import { useState, type FormEvent } from "react";

interface LoginFormProps {
  loading: boolean;
  error: string;
  onSubmit: (data: { email: string; password: string }) => void;
  onForgot: () => void;
}

const baseField =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

export default function LoginForm({ loading, error, onSubmit, onForgot }: LoginFormProps) {
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "");

    const next: { email?: string; password?: string } = {};
    if (!email) next.email = "Ingresa tu correo.";
    if (!password) next.password = "Ingresa tu contraseña.";

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({ email, password });
  };

  const labelClass = "mb-1.5 block text-sm font-label font-medium text-foreground-700";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h1 className="text-xl font-heading font-bold text-foreground-950">Iniciar sesión</h1>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        Accede con tu cuenta de administrador de AXONIA GROUP.
      </p>

      <form className="mt-6 space-y-4" noValidate onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className={labelClass}>
            Correo electrónico
          </label>
          <input id="email" name="email" type="email" autoComplete="email" placeholder="admin@axoniagroup.com" className={baseField} />
          {errors.email && <p className="mt-1 text-xs text-primary-700">{errors.email}</p>}
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="password" className="text-sm font-label font-medium text-foreground-700">
              Contraseña
            </label>
            <button
              type="button"
              onClick={onForgot}
              className="cursor-pointer text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          <input id="password" name="password" type="password" autoComplete="current-password" placeholder="Tu contraseña" className={baseField} />
          {errors.password && <p className="mt-1 text-xs text-primary-700">{errors.password}</p>}
        </div>

        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-base font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden="true"></i>
              Ingresando…
            </>
          ) : (
            "Ingresar"
          )}
        </button>
      </form>
    </div>
  );
}